import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useCompany } from '../context/CompanyContext';
import {
  Plus, Loader2, Calendar, Pencil, Trash2, MoreHorizontal, Clock,
  TrendingUp, CheckCircle2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  shareholder_id: '',
  grant_name: '',
  share_class: 'common',
  total_shares: '',
  vested_shares: '0',
  grant_date: new Date().toISOString().slice(0, 10),
  vesting_start_date: new Date().toISOString().slice(0, 10),
  vesting_end_date: '',
  cliff_months: '0',
  schedule: '1/12 monthly, no cliff',
  status: 'active',
  notes: '',
};

const fmtDate = (d) => (d ? d : '—');
const statusBadge = (s) =>
  s === 'active' ? 'bg-emerald-100 text-emerald-700'
  : s === 'completed' ? 'bg-blue-100 text-blue-700'
  : s === 'paused' ? 'bg-amber-100 text-amber-700'
  : s === 'cancelled' ? 'bg-red-100 text-red-700'
  : 'bg-gray-100 text-gray-700';

export default function VestingPage() {
  const { currentCompany } = useCompany();
  const [schedules, setSchedules] = useState([]);
  const [shareholders, setShareholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const fetchAll = async () => {
    if (!currentCompany?.id) return;
    setLoading(true);
    try {
      const [vRes, sRes] = await Promise.all([
        axios.get(`${API_URL}/api/vesting`, { params: { company_id: currentCompany.id } }),
        axios.get(`${API_URL}/api/shareholders`, { params: { company_id: currentCompany.id } }),
      ]);
      setSchedules(vRes.data || []);
      setShareholders(sRes.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load vesting schedules');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [currentCompany?.id]);

  const openAdd = () => {
    setForm(EMPTY);
    setIsAddOpen(true);
  };

  const openEdit = (sch) => {
    setSelected(sch);
    setForm({
      shareholder_id: sch.shareholder_id || '',
      grant_name: sch.grant_name || '',
      share_class: sch.share_class || 'common',
      total_shares: sch.total_shares?.toString() || '',
      vested_shares: sch.vested_shares?.toString() || '0',
      grant_date: sch.grant_date || '',
      vesting_start_date: sch.vesting_start_date || '',
      vesting_end_date: sch.vesting_end_date || '',
      cliff_months: sch.cliff_months?.toString() || '0',
      schedule: sch.schedule || '',
      status: sch.status || 'active',
      notes: sch.notes || '',
    });
    setIsEditOpen(true);
  };

  const buildPayload = () => ({
    shareholder_id: form.shareholder_id,
    grant_name: form.grant_name || null,
    share_class: form.share_class || 'common',
    total_shares: form.total_shares ? parseFloat(form.total_shares) : 0,
    vested_shares: form.vested_shares ? parseFloat(form.vested_shares) : 0,
    grant_date: form.grant_date || null,
    vesting_start_date: form.vesting_start_date || null,
    vesting_end_date: form.vesting_end_date || null,
    cliff_months: form.cliff_months ? parseInt(form.cliff_months, 10) : 0,
    schedule: form.schedule || 'monthly',
    status: form.status || 'active',
    notes: form.notes || null,
  });

  const handleAdd = async () => {
    if (!form.shareholder_id) { toast.error('Select a shareholder'); return; }
    if (!form.total_shares) { toast.error('Total shares required'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/vesting`, buildPayload());
      toast.success('Vesting schedule created');
      setIsAddOpen(false);
      fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to save');
    } finally { setSubmitting(false); }
  };

  const handleEdit = async () => {
    setSubmitting(true);
    try {
      const payload = buildPayload();
      delete payload.shareholder_id;
      await axios.put(`${API_URL}/api/vesting/${selected.id}`, payload);
      toast.success('Updated');
      setIsEditOpen(false);
      fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update');
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (sch) => {
    if (!window.confirm(`Delete vesting schedule "${sch.grant_name || sch.shareholder_name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/vesting/${sch.id}`);
      toast.success('Deleted');
      fetchAll();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const totals = useMemo(() => {
    const granted = schedules.reduce((s, x) => s + (x.total_shares || 0), 0);
    const vested = schedules.reduce((s, x) => s + (x.vested_shares || 0), 0);
    const unvested = granted - vested;
    const active = schedules.filter((x) => x.status === 'active').length;
    return { granted, vested, unvested, active };
  }, [schedules]);

  if (!currentCompany) return null;

  const renderForm = (
    <div className="space-y-4 py-2">
      <div className="space-y-2">
        <Label>Shareholder *</Label>
        <Select
          value={form.shareholder_id}
          onValueChange={(v) => setForm({ ...form, shareholder_id: v })}
          disabled={isEditOpen}
        >
          <SelectTrigger data-testid="vest-shareholder-select"><SelectValue placeholder="Pick a shareholder" /></SelectTrigger>
          <SelectContent>
            {shareholders.map((sh) => (
              <SelectItem key={sh.id} value={sh.id}>{sh.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Grant Name</Label>
          <Input value={form.grant_name} onChange={(e) => setForm({ ...form, grant_name: e.target.value })} placeholder="e.g. SAC-002" data-testid="vest-grant-name" />
        </div>
        <div className="space-y-2">
          <Label>Share Class</Label>
          <Select value={form.share_class} onValueChange={(v) => setForm({ ...form, share_class: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="common">Common (Employee Vesting)</SelectItem>
              <SelectItem value="preferred">Preferred (Series A)</SelectItem>
              <SelectItem value="founder">Founder</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Total Shares Granted *</Label>
          <Input type="number" step="1" value={form.total_shares} onChange={(e) => setForm({ ...form, total_shares: e.target.value })} placeholder="e.g. 1000" data-testid="vest-total-shares" />
        </div>
        <div className="space-y-2">
          <Label>Already Vested</Label>
          <Input type="number" step="1" value={form.vested_shares} onChange={(e) => setForm({ ...form, vested_shares: e.target.value })} placeholder="0" />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Grant Date</Label>
          <Input type="date" value={form.grant_date} onChange={(e) => setForm({ ...form, grant_date: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Vesting Start</Label>
          <Input type="date" value={form.vesting_start_date} onChange={(e) => setForm({ ...form, vesting_start_date: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Vesting End</Label>
          <Input type="date" value={form.vesting_end_date} onChange={(e) => setForm({ ...form, vesting_end_date: e.target.value })} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Cliff (months)</Label>
          <Input type="number" step="1" min="0" value={form.cliff_months} onChange={(e) => setForm({ ...form, cliff_months: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="paused">Paused</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Schedule</Label>
        <Input value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} placeholder="e.g. 1/48 monthly, 12-month cliff" />
      </div>
      <div className="space-y-2">
        <Label>Notes</Label>
        <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes about this grant" />
      </div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="vesting-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Share Vesting Schedule</h1>
          <p className="text-gray-500 mt-1">Schedule and track equity grants for each shareholder</p>
        </div>
        <Button onClick={openAdd} className="bg-[#1E40AF] hover:bg-[#1E3A8A]" data-testid="add-vesting-btn">
          <Plus className="w-4 h-4 mr-2" /> Schedule Shares
        </Button>
      </div>

      {/* Aggregate stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Active Schedules" value={totals.active} icon={Clock} accent="cyan" />
        <StatCard label="Total Granted" value={totals.granted.toLocaleString()} icon={Calendar} accent="purple" />
        <StatCard label="Vested" value={totals.vested.toLocaleString()} icon={CheckCircle2} accent="green" />
        <StatCard label="Unvested" value={totals.unvested.toLocaleString()} icon={TrendingUp} accent="amber" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : schedules.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No vesting schedules yet</h3>
          <p className="text-gray-500 mb-4">Schedule shares for each shareholder to track grants and vesting.</p>
          <Button onClick={openAdd}><Plus className="w-4 h-4 mr-2" />Schedule Shares</Button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Shareholder</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Grant</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Class</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Vested</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unvested</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Schedule</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 w-12"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {schedules.map((sch) => {
                  const total = sch.total_shares || 0;
                  const vested = sch.vested_shares || 0;
                  const pct = total > 0 ? (vested / total) * 100 : 0;
                  return (
                    <tr key={sch.id} className="hover:bg-gray-50" data-testid={`vest-row-${sch.id}`}>
                      <td className="px-4 py-3 font-medium text-gray-900">{sch.shareholder_name || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{sch.grant_name || '—'}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${sch.share_class === 'preferred' ? 'bg-amber-100 text-amber-700' : sch.share_class === 'founder' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>{sch.share_class}</span></td>
                      <td className="px-4 py-3 text-right text-sm font-medium">{total.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-sm text-emerald-700">{vested.toLocaleString()}</td>
                      <td className="px-4 py-3 text-right text-sm text-gray-600">{(total - vested).toLocaleString()}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{sch.schedule || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{fmtDate(sch.vesting_start_date)}</td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusBadge(sch.status)}`}>{sch.status}</span>
                          <div className="w-20 h-1 bg-gray-200 rounded-full overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, pct)}%` }} /></div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(sch)}><Pencil className="w-4 h-4 mr-2" />Edit</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDelete(sch)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" />Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add Dialog */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Schedule Shares</DialogTitle>
            <DialogDescription>Set up a new vesting schedule for a shareholder.</DialogDescription>
          </DialogHeader>
          {renderForm}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={submitting} data-testid="save-vesting-btn">{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Schedule'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Vesting Schedule</DialogTitle>
          </DialogHeader>
          {renderForm}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
            <Button onClick={handleEdit} disabled={submitting}>{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent = 'cyan' }) {
  const accents = {
    cyan: 'bg-cyan-50 text-cyan-600',
    green: 'bg-emerald-50 text-emerald-600',
    purple: 'bg-purple-50 text-purple-600',
    amber: 'bg-amber-50 text-amber-600',
  };
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-semibold mt-1">{value}</p>
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${accents[accent]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}
